import {
  AfterContentInit,
  Component,
  inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, RouterLink } from '@angular/router';
import {
  AaNgModule,
  DialogContentService,
  DisplayStrategy,
  ICellDefinition,
  IColumnHeaderDefinition,
  INSTITUTION_CONTEXT_TOKEN,
  IPerson,
  IRowDefinition,
  ObjectPropertyDisplayStrategy,
  SearchType,
  SortDirection,
} from 'aa-ng';
import {
  RelevancyProfileComponent as AaNgRelevancyProfileComponent,
  RelevancyProfileView,
} from 'aa-ng-d3';
import { QuicklinkDirective } from 'ngx-quicklink';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  numberComparator,
  sortHelper,
  stringComparator,
} from '../../../helpers/sort-helper';
import { IInstitutionContext } from '../../../models';
import { IActivitySearchResult } from '../../models/activity-search';
import { IPersonSearchResult } from '../../models/person-search';
import { PersonUrlPipe } from '../../pipes/person-url.pipe';
import { DialogScrollService, InstitutionsService } from '../../services';
import { BaseDirective } from '../base.component';
import { RelevancyProfileComponent } from '../relevancy-profile';

interface IListPerson extends IPerson {
  logoPath?: string;
}

@Component({
  selector: 'app-scholars',
  imports: [AaNgModule, PersonUrlPipe, QuicklinkDirective, RouterLink],
  templateUrl: './scholars.component.html',
})
export class ScholarsComponent
  extends BaseDirective
  implements AfterContentInit
{
  institutionsService = inject<InstitutionsService>(INSTITUTION_CONTEXT_TOKEN);
  dialogContentService = inject(DialogContentService);
  route = inject(ActivatedRoute);
  dialogScrollService = inject(DialogScrollService);

  columnHeaderDefinitions: IColumnHeaderDefinition[];
  institutionContext: IInstitutionContext;
  searchType: SearchType;
  nameColumnName = 'Name ';
  relatedWorksColumnName = 'Related Works ';
  relatedNetworksColumnName = 'Related Networks ';
  @Input() showHeader = true;
  @Input() showCounts = true;
  @Input() showInstitutionLogosForSystems = true;
  private _initialSet = false; // Flag to track initial setting
  private _initialPeople: IPerson[];
  @Input() set people(people: IPerson[]) {
    this._people = people;
    this.setPeopleLogoPaths();
    if (!this._initialSet && people) {
      this._initialPeople = [...people];
      this._initialSet = true;
    }
  }
  worksSortDirection = SortDirection.None;
  namesSortDirection = SortDirection.None;
  relatedNetworksSortDirection = SortDirection.None;

  rowDefinition: IRowDefinition<IPerson>;

  activityResultsBehaviorSubject = new BehaviorSubject<IActivitySearchResult[]>(
    null,
  );

  @ViewChild('nameHeader', { static: true })
  emptyHeaderTemplate: TemplateRef<null>;
  @ViewChild('relatedWorksHeader', { static: true })
  relatedWorksHeaderTemplate: TemplateRef<null>;
  @ViewChild('relatedNetworksHeader', { static: true })
  relatedNetworksHeaderTemplate: TemplateRef<null>;

  @ViewChild('logoCell', { static: true })
  logoCellTemplate: TemplateRef<IPerson>;
  @ViewChild('personCell', { static: true })
  personCellTemplate: TemplateRef<IPerson>;
  @ViewChild('relatedWorksCell', { static: true })
  relatedWorksCellTemplate: TemplateRef<IPerson>;
  @ViewChild('relatedNetworksCell', { static: true })
  relatedNetworksCellTemplate: TemplateRef<IPerson>;

  _people: IListPerson[];

  RelevancyProfileView = RelevancyProfileView;

  constructor() {
    super();

    this._subscriptions.push(
      this.institutionsService.context.subscribe((context) => {
        this.institutionContext = context;
        this.setPeopleLogoPaths();
        this.updateColumnDefinitions();
      }),
      this.route.queryParamMap.subscribe((params: ParamMap) => {
        this.searchType =
          params.get('searchType') ?
            SearchType[params.get('searchType') as keyof typeof SearchType]
          : SearchType.Topic;
        this.updateColumnDefinitions();
      }),
    );
  }

  showRelevancyDialog(
    person: IPersonSearchResult,
    activeTab: RelevancyProfileView,
    existingComponent?:
      RelevancyProfileComponent | AaNgRelevancyProfileComponent,
  ) {
    if (existingComponent) {
      this.updateRelevancyProfileComponentSingleTenant(
        existingComponent as RelevancyProfileComponent,
        activeTab,
        person,
      );
    } else {
      this._subscriptions.push(
        this.createRelevancyProfileComponent().subscribe((component) => {
          this._subscriptions.push(
            component.viewToggle.subscribe((type) => {
              this.showRelevancyDialog(person, type, component);
            }),
          );
          this.updateRelevancyProfileComponentSingleTenant(
            component,
            activeTab,
            person,
          );
        }),
      );
    }
  }

  private createRelevancyProfileComponent(): Observable<RelevancyProfileComponent> {
    this.dialogScrollService.dialogOpened();

    return this.dialogContentService.setDialogContent<RelevancyProfileComponent>(
      {
        component: RelevancyProfileComponent,
        dialogClasses: 'large',
        title: 'Relevancy Profile Dialog',
      },
    );
  }

  private updateRelevancyProfileComponentSingleTenant(
    relevancyProfileComponent: RelevancyProfileComponent,
    tab: RelevancyProfileView,
    person: IPersonSearchResult,
  ) {
    if (person && !relevancyProfileComponent._person) {
      relevancyProfileComponent.person = person;
    }
    relevancyProfileComponent.currentTab =
      tab === RelevancyProfileView.Works ?
        relevancyProfileComponent.RelevancyProfileView.Works
      : relevancyProfileComponent.RelevancyProfileView.Collaborations;
  }

  ngAfterContentInit() {
    this.updateColumnDefinitions();
  }

  private setPeopleLogoPaths() {
    if (
      this._people &&
      this.institutionContext &&
      this.institutionContext.institutionGroup
    ) {
      this._people.forEach((person: IListPerson) => {
        if (this.institutionContext.institutionGroup) {
          const institution =
            this.institutionContext.institutionGroup.institutions.find(
              (inst) => inst.id === person.primaryInstitution.id,
            );
          person.logoPath = institution.logoPath;
        }
      });
    }
  }

  private updateColumnDefinitions() {
    const columnHeaderDefinitions: IColumnHeaderDefinition[] = [];
    const cellDefinitions: ICellDefinition<IPerson>[] = [];

    if (
      this.institutionContext &&
      this.institutionContext.institutionGroup &&
      this.showInstitutionLogosForSystems
    ) {
      cellDefinitions.push({
        displayStrategy: new ObjectPropertyDisplayStrategy<IPerson>(
          this.logoCellTemplate,
        ),
        classes: 'institution-logo',
      });
    }

    cellDefinitions.push({
      displayStrategy: new ObjectPropertyDisplayStrategy<IPerson>(
        this.personCellTemplate,
      ),
      classes: 'people-name',
    });

    if (this.showCounts && this.searchType === SearchType.Topic) {
      cellDefinitions.push(
        {
          displayStrategy: new ObjectPropertyDisplayStrategy<IPerson>(
            this.relatedWorksCellTemplate,
          ),
          classes: 'relevancy-count',
        },
        {
          displayStrategy: new ObjectPropertyDisplayStrategy<IPerson>(
            this.relatedNetworksCellTemplate,
          ),
          classes: 'relevancy-count',
        },
      );
    }

    if (
      this.institutionContext &&
      this.institutionContext.institutionGroup &&
      this.showInstitutionLogosForSystems
    ) {
      columnHeaderDefinitions.push({
        displayStrategy: new DisplayStrategy(this.emptyHeaderTemplate),
      });
    }

    columnHeaderDefinitions.push({
      displayStrategy: new DisplayStrategy(this.emptyHeaderTemplate),
      width: this.searchType === SearchType.Topic ? '1fr' : '7fr',
    });

    if (
      this.showCounts &&
      this.showHeader &&
      this.searchType === SearchType.Topic
    ) {
      columnHeaderDefinitions.push(
        {
          displayStrategy: new DisplayStrategy(this.relatedWorksHeaderTemplate),
        },
        {
          displayStrategy: new DisplayStrategy(
            this.relatedNetworksHeaderTemplate,
          ),
        },
      );
    }

    this.columnHeaderDefinitions = columnHeaderDefinitions;
    this.rowDefinition = { cellDefinitions };
  }
  sort(event: SortDirection, columnName: string) {
    this.worksSortDirection = SortDirection.None;
    this.namesSortDirection = SortDirection.None;
    this.relatedNetworksSortDirection = SortDirection.None;

    switch (columnName) {
      case this.relatedWorksColumnName:
        this.worksSortDirection = event;
        break;
      case this.nameColumnName:
        this.namesSortDirection = event;
        break;
      case this.relatedNetworksColumnName:
        this.relatedNetworksSortDirection = event;
        break;
    }

    this.updateSort();
  }

  updateSort() {
    if (
      [
        this.worksSortDirection,
        this.namesSortDirection,
        this.relatedNetworksSortDirection,
      ].some((x) => x !== SortDirection.None)
    ) {
      let data = this._people;
      data = sortHelper(
        data,
        this.worksSortDirection,
        numberComparator((x) => x.relevantWorksCount),
      );
      data = sortHelper(
        data,
        this.namesSortDirection,
        stringComparator((x) => x.lastName),
      );
      data = sortHelper(
        data,
        this.relatedNetworksSortDirection,
        numberComparator((x) => x.relevantCollaborativeWorksCount),
      );

      this._people = data;
    } else {
      this._people = this._initialPeople;
    }
  }
}
